import { Button } from '../design/Button'
import { Dialog } from '../design/Dialog'
import { useAuth } from '../shell/AuthContext'
import { useInactivity } from '../shell/useInactivity'

/**
 * The idle-session dialog — `Components/Shared/InactivityTracker.razor` plus
 * `InactivityWarningModal.razor`, as one component because the split bought nothing: the tracker's
 * whole body was subscribing to the modal's two buttons.
 *
 * Mounted by `App`, which is the **standalone** portal. Not by `RelayModule`: federated, the host
 * owns the session, and a remote that signs the user out of the host's shell on its own schedule is
 * a module deciding something that is not its to decide.
 *
 * It is rendered whether or not anybody is signed in and does nothing until they are, because the
 * alternative — mounting it on sign-in — restarts the timers on every change to the auth context.
 */
export function InactivityWarning() {
  const { isAuthenticated, logout } = useAuth()
  const { warning, remainingSeconds, stay } = useInactivity({
    onLogout: logout,
    enabled: isAuthenticated,
  })

  if (!warning) return null

  return (
    <Dialog
      title="Session Timeout Warning"
      size="sm"
      closeOnOverlayClick={false}
      // Any dismissal is the operator saying they are here, which is the whole question.
      onClose={stay}
      footer={
        <>
          <Button variant="danger" onClick={logout}>
            Sign out now
          </Button>
          <Button variant="primary" onClick={stay}>
            Stay signed in
          </Button>
        </>
      }
    >
      <p className="text-sm text-fg">You have been inactive for a while.</p>
      <p className="mt-sm text-sm text-fg">
        <strong>
          You will be signed out in {remainingSeconds} second{remainingSeconds === 1 ? '' : 's'}
        </strong>{' '}
        for your security.
      </p>
      <p className="mt-sm text-sm text-fg-secondary">
        Choose “Stay signed in” to carry on where you left off.
      </p>
    </Dialog>
  )
}
